/**
 * ActionChecklistCard — grouped action checklist for a judgment.
 *
 * Consumes GET /api/debtors/:id/checklist. Renders items grouped by
 * Lien Filings · Renewal Actions · Dormancy Watch · State-Specific.
 * Toggling a checkbox PATCHes /api/debtors/:id/checklist.
 */
import React, { useEffect, useState } from "react";
import { Check, HelpCircle, ListChecks } from "lucide-react";
import { apiClient, formatApiError } from "@/lib/apiClient";
import { toast } from "sonner";
import JeaCard from "@/components/jea/JeaCard";
import { useRolePermissions } from "@/hooks/useRolePermissions";
import { cn } from "@/lib/utils";

const GROUPS = [
  { key: "lien",           label: "Lien Filings" },
  { key: "renewal",        label: "Renewal Actions" },
  { key: "dormancy",       label: "Dormancy Watch" },
  { key: "state_specific", label: "State-Specific" },
];

const priorityBorder = (priority) => {
  if (priority === "critical") return "border-l-danger";
  if (priority === "high") return "border-l-warning";
  if (priority === "medium") return "border-l-accent-pink";
  return "border-l-border-default";
};

const ChecklistItem = ({ item, onToggle, disabled }) => {
  return (
    <div
      className={cn(
        "flex items-start gap-3 rounded-[8px] bg-bg-tertiary border-l-4 p-3",
        priorityBorder(item.priority),
      )}
      data-testid={`checklist-item-${item.id}`}
    >
      <button
        type="button"
        onClick={() => !disabled && onToggle(item)}
        disabled={disabled}
        className={cn(
          "shrink-0 w-5 h-5 rounded-[4px] border flex items-center justify-center transition-colors",
          item.completed
            ? "bg-accent-pink border-transparent"
            : "border-text-secondary hover:border-accent-pink",
          disabled && "opacity-40 cursor-not-allowed",
        )}
        data-testid={`checklist-toggle-${item.id}`}
        aria-checked={item.completed}
        role="checkbox"
      >
        {item.completed && <Check size={12} className="text-[#091424]" />}
      </button>
      <div className="min-w-0 flex-1">
        <div className={cn("text-[13px] font-semibold", item.completed ? "text-text-secondary line-through" : "text-white")}>
          {item.title}
        </div>
        {item.detail && (
          <div className="text-[12px] text-text-secondary mt-1 leading-4">{item.detail}</div>
        )}
        {item.tags?.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1">
            {item.tags.map((t) => (
              <span key={t} className="text-[10px] px-1.5 py-0.5 rounded bg-bg-primary text-text-muted uppercase tracking-[0.06em]">
                {t.replace(/_/g, " ")}
              </span>
            ))}
          </div>
        )}
      </div>
      {item.help_text && (
        <span title={item.help_text} className="shrink-0 text-text-secondary hover:text-accent-pink cursor-help">
          <HelpCircle size={12} />
        </span>
      )}
    </div>
  );
};

export const ActionChecklistCard = ({ debtorId, testid = "action-checklist-card" }) => {
  const { canMutateDebtors } = useRolePermissions();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get(`/debtors/${debtorId}/checklist`);
      setData(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (debtorId) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debtorId]);

  const toggle = async (item) => {
    setSaving(true);
    try {
      // Optimistic update
      const next = { ...data };
      next.groups = { ...data.groups };
      for (const grp of GROUPS) {
        next.groups[grp.key] = (data.groups[grp.key] || []).map((i) =>
          i.id === item.id ? { ...i, completed: !i.completed } : i
        );
      }
      setData(next);
      await apiClient.patch(`/debtors/${debtorId}/checklist`, {
        checklist_state: { [item.id]: !item.completed },
      });
    } catch (e) {
      toast.error(formatApiError(e));
      load(); // rollback
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <JeaCard className="min-h-[200px] animate-pulse" data-testid={`${testid}-loading`}>
        <div className="jea-caption">Action Checklist</div>
      </JeaCard>
    );
  }
  if (!data) return null;

  const total = Object.values(data.groups || {}).reduce((sum, g) => sum + g.length, 0);
  const done = Object.values(data.groups || {}).reduce(
    (sum, g) => sum + g.filter((i) => i.completed).length,
    0
  );

  return (
    <JeaCard data-testid={testid}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <ListChecks size={16} className="text-accent-pink" />
          <div className="text-white font-semibold text-[15px]">
            Action Checklist — {data.state_name || "This Judgment"}
          </div>
        </div>
        <div className="text-[12px] text-text-secondary jea-mono">
          {done} / {total} complete
        </div>
      </div>
      <div className="space-y-6">
        {GROUPS.map((grp) => {
          const items = data.groups?.[grp.key] || [];
          if (!items.length) return null;
          return (
            <div key={grp.key} data-testid={`checklist-group-${grp.key}`}>
              <div className="jea-caption mb-2">{grp.label}</div>
              <div className="space-y-2">
                {items.map((item) => (
                  <ChecklistItem
                    key={item.id}
                    item={item}
                    onToggle={toggle}
                    disabled={saving || !canMutateDebtors}
                  />
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </JeaCard>
  );
};

export default ActionChecklistCard;
